import { uniq } from "lodash";
import { ConsentPurpose } from "../enums/email-consent-purpose";
import { isGatedPurpose } from "../gdpr/email-consent-mapping";

/**
 * Longest SMS text we send: two concatenated GSM-7 segments of 153 septets.
 * A longer text is split into three or more parts, each billed separately.
 */
export const SMS_MAX_GSM7_LENGTH = 306;

/** GSM 03.38 basic character table (minus the escape code). One septet each. */
const GSM7_BASIC_CHARS =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";

/** GSM 03.38 extension table. Sent as escape + char, so two septets each. */
const GSM7_EXTENSION_CHARS = "^{}\\[~]|€";

const basic = new Set(Array.from(GSM7_BASIC_CHARS));
const extension = new Set(Array.from(GSM7_EXTENSION_CHARS));

/** Put before the unsubscribe short link appended to gated bulk SMS. */
export const SMS_UNSUBSCRIBE_LINK_PREFIX = "\nAvregistrera: ";

/**
 * Septets the unsubscribe suffix takes: prefix + `https://s.bonliva.se/` + a
 * five-character `[0-9][a-z]{4}` code (see `generateShortCode`).
 */
export const SMS_UNSUBSCRIBE_LINK_RESERVE = getGsm7Length(
  `${SMS_UNSUBSCRIBE_LINK_PREFIX}https://s.bonliva.se/0abcd`
);

/**
 * How long the typed text of a bulk SMS may be. When the worker appends the
 * unsubscribe link (flag on, gated purpose) the link's room comes off the 306,
 * so the full message still fits two segments and the check can run when the
 * action is queued rather than per recipient in the job.
 */
export function bulkSmsTextMaxLength(
  purpose: ConsentPurpose,
  unsubscribeLinkEnabled: boolean
): number {
  return unsubscribeLinkEnabled && isGatedPurpose(purpose)
    ? SMS_MAX_GSM7_LENGTH - SMS_UNSUBSCRIBE_LINK_RESERVE
    : SMS_MAX_GSM7_LENGTH;
}

export type SmsTextValidation = {
  valid: boolean;
  /** Length in GSM-7 septets; extension-table characters count as two. */
  length: number;
  maxLength: number;
  tooLong: boolean;
  /** Distinct characters outside GSM-7 (emojis, typographic quotes, ...), in order of appearance. */
  invalidCharacters: string[];
};

/**
 * Characters that would force the gateway out of GSM-7 into UCS-2. Iterates
 * by code point so an emoji is reported as one character, not two halves.
 */
export function getNonGsm7Characters(text: string): string[] {
  return uniq(Array.from(text ?? "").filter((c) => !basic.has(c) && !extension.has(c)));
}

/** Septet count of the text; characters outside GSM-7 count as one. */
export function getGsm7Length(text: string): number {
  return Array.from(text ?? "").reduce((sum, c) => sum + (extension.has(c) ? 2 : 1), 0);
}

export function validateSmsText(
  text: string,
  maxLength = SMS_MAX_GSM7_LENGTH
): SmsTextValidation {
  const invalidCharacters = getNonGsm7Characters(text);
  const length = getGsm7Length(text);
  const tooLong = length > maxLength;

  return {
    valid: invalidCharacters.length === 0 && !tooLong,
    length,
    maxLength,
    tooLong,
    invalidCharacters
  };
}

/** English, for logs and API errors. The UI renders its own localized text. */
export function describeSmsTextValidation(result: SmsTextValidation): string {
  const reasons: string[] = [];
  if (result.invalidCharacters.length > 0) {
    reasons.push(
      `contains characters not allowed in SMS: ${result.invalidCharacters.join(" ")}`
    );
  }
  if (result.tooLong) {
    reasons.push(`is ${result.length} characters long, max ${result.maxLength}`);
  }
  return `SMS text ${reasons.join(" and ")}`;
}
